import { ChangeDetectionStrategy, Component, inject, Injector, type TemplateRef, viewChild } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { Application } from '@modules/common/types/application';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { NavAddMenuComponent } from '@modules/pages/components/nav-add-menu/nav-add-menu.component';
import { Subject } from 'rxjs';
import { BoardListStore } from '../../services/board-list.store';
import { BoardsDialogService } from '../../services/boards-dialog.service';
import { BoardsStore } from '../../services/boards.store';
import { createBlankBoard } from '../creation/create-blank-board';
import { BoardCreateOptionsComponent } from '../creation/board-create-options/board-create-options.component';
import { NewBoardComponent } from '../creation/new-board/new-board.component';
import { injectBoardOpener } from '../shared/board-opener';

/**
 * The rail's New board button and global Add (adapted from `SlidesRailControlsComponent`). Blank, template and import
 * all end in the one opener the layout provides (`BOARD_OPENER`: the guarded open that starts the full-window
 * session only once it has succeeded), so a creation can never fold anything before its board is shown.
 */
@Component({
  selector: 'app-boards-rail-controls',
  templateUrl: './boards-rail-controls.component.html',
  host: { class: 'hidden' },
  styleUrl: './boards-rail-controls.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button2Component, ContextMenuItemComponent, ContextMenuWindowComponent, IconComponent, NavAddMenuComponent, PopoverDirective],
})
export class BoardsRailControlsComponent {
  public readonly actionsTemplate = viewChild<TemplateRef<unknown>>('actions');
  protected readonly store = inject(BoardsStore);
  protected readonly listStore = inject(BoardListStore);
  private readonly modalService = inject(BoardsDialogService);
  private readonly injector = inject(Injector);
  private readonly openBoard = injectBoardOpener();
  protected readonly newBoardPopoverClose = new Subject<void>();
  protected readonly app = Application.boards;

  private blocked(): boolean {
    return this.injector.get(AccessLimitGateService).blocks('createItem', this.injector);
  }

  protected async createBlank(): Promise<void> {
    if (this.blocked()) return;
    this.newBoardPopoverClose.next();
    const board = await createBlankBoard(this.store, this.listStore); // a second click while this is out reuses it (the store's keyed create)
    if (board) this.openBoard(board.id);
  }

  protected createFromTemplate(): void {
    if (this.blocked()) return;
    this.newBoardPopoverClose.next();
    this.modalService.showModal({
      component: BoardCreateOptionsComponent,
      injector: this.injector,
      title: 'From a template',
      width: 600,
      content: { folderId: this.listStore.folderId() },
    });
  }

  protected importBoard(): void {
    if (this.blocked()) return;
    this.newBoardPopoverClose.next();
    this.modalService.showModal({
      component: NewBoardComponent,
      injector: this.injector,
      title: 'Import a board',
      width: 600,
      content: { folderId: this.listStore.folderId() },
    });
  }
}
